/**
 * Makes it easier to track changes and state in the application over time
 * @example
 * const sampleFieldNames = ["ham", "cheese"] as const;
 * type SampleType = typeof sampleFieldNames[number];
 *
 * xray.add<SampleType>("sandwich", sampleFieldNames);
 *
 * xray.named<xrayType>("sandwich").addSnapshot("Added extra cheese", {
 *  cheese: 2
 * });
 * xray.named("sandwich")
 * .toConsole()
 * .clear();
 */
export var xray;
(function (xray) {
    xray.DefaultTimeSeriesXRayOptions = {
        logEvents: true,
        logSnapshots: true
    };
    /**
     * An easy way to track how data is changed and what state it is in
     * across multiple steps of a process
     * @example
     * const sampleFieldNames = ["ham", "cheese"] as const;
     * type SampleType = typeof sampleFieldNames[number];
     *
     * const xray = new TimeSeriesXray<SampleType>(sampleFieldNames);
     * xray.addSnapshot()
     */
    class TimeSeriesXRay {
        /**
         * Fields to be tracked
         */
        name;
        fields;
        timeline = [];
        logEvents;
        logSnapshots;
        fieldLookupMethods = {};
        constructor(fields, options) {
            const { name, logEvents, logSnapshots } = { ...xray.DefaultTimeSeriesXRayOptions, ...options };
            this.name = name;
            this.logEvents = logEvents;
            this.logSnapshots = logSnapshots;
            this.fields = fields;
        }
        getFieldMapTemplate() {
            const fieldMap = {};
            for (const field of this.fields) {
                fieldMap[field] = "";
            }
            return fieldMap;
        }
        setFieldLookup(name, lookupMethod) {
            this.fieldLookupMethods[name] = lookupMethod;
            return this;
        }
        addEvent(name, value) {
            let record = {
                name,
                timestamp: new Date(),
                type: "event",
                eventValue: value
            };
            if (this.logEvents) {
                console.log(`[xray] ${this.name}: Add event ${name}`, record);
            }
            this.timeline.push(record);
            return this;
        }
        /**
         * Gets the current value of each field and maps it to a field hash
         * @returns
         */
        lookupAllFields() {
            let keys = Object.keys(this.fieldLookupMethods);
            let fields = {};
            for (let key of keys) {
                fields[key] = this.fieldLookupMethods[key]?.();
            }
            return fields;
        }
        /**
         * Captures a snapshot of the current field values
         * @param name
         */
        captureSnapshot(name) {
            let fields = this.lookupAllFields();
            let record = {
                name,
                timestamp: new Date(),
                type: "snapshot",
                fields
            };
            if (this.logSnapshots) {
                console.log(`[xray] ${this.name}: Add snapshot ${name}`, record);
            }
            this.timeline.push(record);
            return this;
        }
        toConsole() {
            let mapped = this.timeline.map((entry) => {
                let name, fields;
                if (entry.type === "snapshot") {
                    name = entry.name;
                    fields = { ...this.getFieldMapTemplate(), ...entry.fields };
                }
                else {
                    name = `${entry.name}: ${entry.eventValue}`;
                    fields = this.getFieldMapTemplate();
                }
                return {
                    name,
                    ...fields,
                    timestamp: entry.timestamp.toString()
                };
            });
            console.log(`[xray] ${this.name}: ${mapped.length} entries`);
            console.table(mapped);
            return this;
        }
        clear() {
            while (this.timeline.length > 0) {
                this.timeline.pop();
            }
            return this;
        }
    }
    xray.TimeSeriesXRay = TimeSeriesXRay;
    function named(name) {
        let result = xray.global[name];
        if (!result) {
            throw new Error(`No xray named ${name}`);
        }
        return result;
    }
    xray.named = named;
    function add(name, fields) {
        if (xray.global[name]) {
            throw new Error(`${name} xray already exists`);
        }
        xray.global[name] = new TimeSeriesXRay(fields, { name });
        return xray.global[name];
    }
    xray.add = add;
    xray.global = {};
})(xray || (xray = {}));
//# sourceMappingURL=xray.js.map